<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading :module-id="access.module_id" :profile-id="access.profile_id" :reference="access.view_reference" summary-only />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <AccountingHelp view="accountingEntries" />
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }" v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-dropdown right no-caret size="sm" variant="link" toggle-class="list-view-icon-btn list-view-icon-btn--primary" v-b-tooltip.hover.bottom title="Nuevo asiento y acciones del libro">
                <template #button-content><AppIcon name="add" label="Nuevo" /></template>
                <b-dropdown-item @click="openManualAccounting()"><AppIcon name="edit_note" class="mr-1" />Asiento manual</b-dropdown-item>
                <b-dropdown-item :disabled="!bookSelected || !bookStatus || !bookStatus.period_start" @click="openInventory()"><AppIcon name="box" class="mr-1" />Asiento de inventario</b-dropdown-item>
                <b-dropdown-item :disabled="!bookSelected || !bookStatus || !bookStatus.period_start" @click="openIva()"><AppIcon name="calculator" class="mr-1" />Liquidación de IVA</b-dropdown-item>
                <b-dropdown-item v-if="bookSelected && bookStatus && !bookStatus.period_start" @click="$refs.openingForm.open()"><AppIcon name="playlist_add" class="mr-1" />Apertura inicial</b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item :disabled="!bookSelected" @click="$refs.monthLock.open()"><AppIcon name="lock" class="mr-1" />Bloqueo mensual</b-dropdown-item>
                <b-dropdown-text v-if="!bookSelected" class="small text-muted">Para inventario, IVA y bloqueo, elegí Declarado o No declarado arriba.</b-dropdown-text>
              </b-dropdown>
            </div>
          </div>
        </section>
        <AccountingBookBar :businesses="arr.businesses" note="Los asientos manuales nuevos se cargan en este libro." @change="onBookChange($event)">
          <template v-if="bookStatus">
            <template v-if="!bookStatus.period_start">Sin apertura: cargala desde + → Apertura inicial.</template>
            <template v-else>
              Ejercicio desde el {{ formatShortDate(bookStatus.period_start) }}
              <span v-if="bookStatus.locked_until"> · bloqueado hasta el {{ formatShortDate(bookStatus.locked_until) }}</span>
              <span v-if="bookStatus.last_iva"> · IVA liquidado hasta {{ formatShortDate(bookStatus.last_iva.date) }}</span>
              <span v-if="bookStatus.last_inventory"> · último inventario {{ formatShortDate(bookStatus.last_inventory.date) }}</span>
            </template>
          </template>
        </AccountingBookBar>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width:170px;"><label class="filter-label">Referencia</label><b-form-input type="text" size="sm" v-model="filters.reference" placeholder="Buscar..." @keyup.enter="filterReportReset()"></b-form-input></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Desde</label><b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Hasta</label><b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input></div>
            <div class="list-view-filter" style="width:120px;"><label class="filter-label">Estado</label><b-form-select size="sm" v-model="filters.status" :options="arr.select.status"></b-form-select></div>
            <div class="list-view-filter" style="width:120px;"><label class="filter-label">Origen</label><b-form-select size="sm" v-model="filters.origin" :options="arr.select.origin"></b-form-select></div>
            <div class="list-view-filter" style="width:190px;"><label class="filter-label">Movimiento</label><b-form-select size="sm" v-model="filters.source_type" :options="arr.select.sourceType"></b-form-select></div>
            <div class="list-view-filter" style="width:200px;"><label class="filter-label">Cuenta contable</label><b-form-select size="sm" v-model="filters.accounting_accounts_id" :options="arr.leafAccounts"></b-form-select></div>
            <b-button variant="outline-dark" size="sm" @click="filterReportReset()" v-b-tooltip.hover title="Aplicar filtros" style="height:31px;width:31px;padding:0;flex-shrink:0;"><AppIcon name="filter" /></b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row class="mt-3">
          <b-col>
            <b-table class="app-table mb-0"
                    sticky-header="60vh"
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :fields="table.fields"
                    :filter="table.filter"
                    :current-page="table.currentPage"
                    :per-page="table.perPage"
                    :items="table.items"
                    :tbody-tr-class="entryRowClass"
                    @row-clicked="openDetail"
                    v-if="table.items.length">

              <template v-slot:cell(date)="data">
                <div v-if="data.item.date">
                  {{ moment(data.item.date).format('DD MMMM YYYY') }}
                </div>
              </template>

              <template v-slot:cell(book)="data">
                <AccountingBookBadge :declared="data.item.declared" />
              </template>

              <template v-slot:cell(reference)="data">
                {{data.item.reference}}
              </template>

              <template v-slot:cell(observations)="data">
                {{data.item.observations}}
              </template>

              <template v-slot:cell(origin)="data">
                <b-badge :variant="originVariant(data.item.origin)" style="font-size:12px; padding:4px 8px;">
                  {{ originLabel(data.item.origin) }}
                </b-badge>
              </template>

              <template v-slot:cell(total_debit)="data">
                <b>{{ formatCurrency(data.item.total_debit) }}</b>
              </template>

              <template v-slot:cell(total_credit)="data">
                <b>{{ formatCurrency(data.item.total_credit) }}</b>
                <b-badge v-if="!data.item.is_balanced" variant="danger" class="d-block mt-1">Diferencia de {{ formatCurrency(Math.abs(data.item.difference)) }}</b-badge>
              </template>

              <template v-slot:cell(f_action)="data">
                <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle">
                  <template #button-content><AppIcon name="more_vert" label="Acciones" /></template>
                  <b-dropdown-item @click="openDetail(data.item)"><AppIcon name="visibility" /> Ver detalle</b-dropdown-item>
                  <template v-if="['manual', 'opening'].includes(data.item.origin) && !isPeriodLocked(data.item.date)">
                    <b-dropdown-item @click="openManualAccounting(data.item)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                    <b-dropdown-item @click="deleteManualAccounting(data.item)"><AppIcon name="delete" /> Eliminar</b-dropdown-item>
                  </template>
                  <b-dropdown-item v-if="data.item.origin == 'automatic' && data.item.total_debit > 0 && !isPeriodLocked(data.item.date)" @click="revertApproved(data.item)"><AppIcon name="undo" /> Borrar asiento</b-dropdown-item>
                  <b-dropdown-item v-if="['inventory', 'iva'].includes(data.item.origin) && !isPeriodLocked(data.item.date)" @click="deleteManualAccounting(data.item)"><AppIcon name="delete" /> Borrar asiento</b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <EmptyState v-else icon="receipt_long" title="Sin asientos" description="No se encontraron asientos con estos filtros." />
            <div class="app-table-footer" v-if="table.tablePaginate">
              <span class="list-view-toolbar__count">{{ table.tablePaginate.total }} registros</span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
          <nav v-if="table.tablePaginate">
            <b-pagination class="mb-0" size="sm" pills="" :total-rows="table.tablePaginate.total" :per-page="table.tablePaginate.per_page" v-model="table.currentPage" @input="filterReport()" />
          </nav>
        </div>
      </b-card>

      <b-modal v-model="modal.manual.active"
               size="xl"
               :key="'manual_' + modal.manual._id"
               :id="modal.manual._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit_note" /></span>
          <h5 class="app-modal-header__title">{{ modal.manual.title }}</h5>
        </div>

        <b-row>
          <b-col md="2">
            <b-form-group label="Empresa *">
              <b-form-select size="sm" v-model="crud.manual.business_id" :options="arr.businesses"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Libro *">
              <b-form-select size="sm" v-model="crud.manual.declared" :options="arr.select.declaredRequired"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Fecha">
              <b-form-input type="date" size="sm" v-model="crud.manual.date"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Referencia">
              <b-form-input type="text" size="sm" v-model="crud.manual.reference" placeholder="Ej: AJUSTE-0001"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Observaciones">
              <app-textarea size="sm" v-model="crud.manual.observations" rows="2" max-rows="6"></app-textarea>
            </b-form-group>
          </b-col>
          <b-col md="12" class="mb-2" v-if="crud.manual.origin == 'manual'">
            <div class="entry-templates">
              <b-dropdown size="sm" variant="outline-dark" :disabled="!arr.templates.length">
                <template #button-content><AppIcon name="bookmark" class="mr-1" />Usar modelo</template>
                <b-dropdown-text v-if="!arr.templates.length" class="small text-muted">Todavía no hay modelos</b-dropdown-text>
                <b-dropdown-item v-for="template in arr.templates" :key="template.id" @click="applyTemplate(template)" link-class="entry-templates__item">
                  <span>{{ template.name }} <small class="text-muted">· {{ template.lines.length }} renglones</small></span>
                  <span class="entry-templates__delete" v-b-tooltip.hover title="Borrar modelo" @click.stop="deleteTemplate(template)"><AppIcon name="delete" /></span>
                </b-dropdown-item>
              </b-dropdown>
              <b-button size="sm" variant="link" class="ml-1" @click="openSaveTemplate()"><AppIcon name="bookmark-filled" class="mr-1" />Guardar como modelo</b-button>
              <small class="text-muted ml-2" v-if="crud.manual.template">Modelo «{{ crud.manual.template }}»: completá o corregí los importes.</small>
            </div>
          </b-col>
          <b-col md="12">
            <AccountingEntryBuilder
              ref="manualBuilder"
              :key="'manual_accounting_' + manualAccountingKey"
              typeDebe=""
              typeHaber=""
              :defaultDebe="crud.manual.defaultDebe"
              :defaultHaber="crud.manual.defaultHaber"
              :reference="crud.manual.reference"
              :date="crud.manual.date"
              :declared="crud.manual.declared"
              :amountTotal="0"
              validationMode="manual"
              :showConstructor="true"
              :editableAmounts="crud.manual.origin == 'opening' || !!crud.manual.template"
              @getAsiento="loadManualAccountingDetail($event)" />
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.manual.active=false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" @click="saveManualAccounting()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </div>
      </b-modal>

      <AccountingInventoryModal ref="inventory" :business-id="filters.business_id" :declared="filters.declared === '1'" @saved="onBookActionDone()" />
      <b-modal v-model="modal.template.active" size="sm" :id="modal.template._id" centered>
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="bookmark-filled" /></span>
          <h5 class="app-modal-header__title">Guardar como modelo</h5>
        </div>
        <b-form-group label="Nombre del modelo *" description="Con un nombre que ya existe, se reemplaza ese modelo." class="mb-2">
          <b-form-input size="sm" v-model="modal.template.name" list="accounting-template-names" placeholder="Ej: Sueldos mensuales" @keyup.enter="saveTemplate()" />
          <datalist id="accounting-template-names"><option v-for="template in arr.templates" :key="template.id" :value="template.name" /></datalist>
        </b-form-group>
        <b-form-checkbox v-model="modal.template.keepAmounts">Guardar también los importes</b-form-checkbox>
        <small class="text-muted d-block">Sin importes, el modelo trae las cuentas en cero y los completás cada vez.</small>
        <div slot="modal-footer">
          <b-button variant="outline-secondary" size="sm" class="mr-1" @click="modal.template.active = false">Cancelar</b-button>
          <b-button variant="dark" size="sm" :disabled="!modal.template.name.trim()" @click="saveTemplate()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </div>
      </b-modal>

      <AccountingIvaModal ref="iva" :business-id="filters.business_id" :declared="filters.declared === '1'" @saved="onBookActionDone()" />
      <AccountingMonthLockModal ref="monthLock" :business-id="filters.business_id" :declared="filters.declared === '1'" :can-unlock="canUnlock" @changed="onBookActionDone()" />
      <AccountingOpeningForm ref="openingForm" :business-id="filters.business_id" :declared="filters.declared === '1'" @saved="onBookActionDone()" />

      <b-modal v-model="modal.detail.active"
               size="lg"
               :key="'detail_' + modal.detail._id"
               :id="modal.detail._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{ modal.detail.title }}</h5>
        </div>

        <div v-if="selectedAccounting">
          <p class="small text-muted mb-1" v-if="selectedAccounting.approved_at">
            Aprobado el {{ moment(selectedAccounting.approved_at).format('DD/MM/YYYY HH:mm') }}<span v-if="selectedAccounting.approved_user_name"> por {{ selectedAccounting.approved_user_name }}</span><span v-if="selectedAccounting.suggestion_edited">, con cambios sobre la sugerencia</span>.
          </p>
          <div class="d-flex flex-wrap mb-3 px-3 py-2 rounded border" style="background:var(--app-color-surface-subtle);">
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Fecha</small>
              <span>{{ moment(selectedAccounting.date).format('DD/MM/YYYY') }}</span>
            </div>
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Referencia</small>
              <span>{{ selectedAccounting.reference }}</span>
            </div>
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Libro</small>
              <AccountingBookBadge :declared="selectedAccounting.declared" />
            </div>
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Origen</small>
              <b-badge :variant="selectedAccounting.origin == 'manual' ? 'info' : 'secondary'" style="font-size:12px; padding:3px 8px;">
                {{ selectedAccounting.origin_movement ? selectedAccounting.origin_movement.label : selectedAccounting.origin }}
              </b-badge>
            </div>
            <div>
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Estado</small>
              <b-badge :variant="selectedAccounting.is_balanced ? 'success' : 'danger'" style="font-size:12px; padding:3px 8px;">
                {{ selectedAccounting.is_balanced ? 'Balanceado' : 'Con diferencia' }}
              </b-badge>
            </div>
          </div>

          <AccountingEntrySource v-if="selectedAccounting.source_type" :accounting-id="selectedAccounting.id" />

          <b-table class="app-table app-table--compact mb-0"
                   responsive="sm"
                   :hover="true"
                   :small="true"
                   :items="tableSub.items"
                   :fields="tableSub.fields"
                   :tbody-tr-class="subTableRowClass"
                   v-if="tableSub.items.length">
            <template v-slot:cell(code)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{data.item.accounting_accounts_code}}
              </span>
            </template>
            <template v-slot:cell(name)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{data.item.accounting_accounts_name}}
              </span>
            </template>
            <template v-slot:cell(debit)="data">
              <b v-if="data.item.type == 'Debe'" class="accounting-entries-amount">{{ formatCurrency(data.item.total) }}</b>
            </template>
            <template v-slot:cell(credit)="data">
              <b v-if="data.item.type == 'Haber'" class="accounting-entries-amount">{{ formatCurrency(data.item.total) }}</b>
            </template>
          </b-table>
          <EmptyState v-else icon="receipt_long" title="Sin renglones" description="Este asiento todavía no tiene renglones." />

          <div class="d-flex justify-content-end mt-3">
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:var(--app-color-warning-soft);">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Debe</small>
              <strong style="font-size:15px;">{{ formatCurrency(selectedAccounting.total_debit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:var(--app-color-info-soft);">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Haber</small>
              <strong style="font-size:15px;">{{ formatCurrency(selectedAccounting.total_credit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2" style="min-width:150px;"
                 :style="{ background: selectedAccounting.is_balanced ? 'var(--app-color-success-soft)' : 'var(--app-color-danger-soft)' }">
              <small class="d-block font-weight-bold text-uppercase" style="font-size:10px;"
                     :class="selectedAccounting.is_balanced ? 'text-success' : 'text-danger'">Diferencia</small>
              <strong style="font-size:15px;"
                      :class="selectedAccounting.is_balanced ? 'text-success' : 'text-danger'">{{ formatCurrency(selectedAccounting.difference) }}</strong>
            </div>
          </div>
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" @click="modal.detail.active=false"><AppIcon name="close" class="mr-1" />Cerrar</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>
<script>
  import AccountingHelp from './inc/AccountingHelp'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import StorageLocal from '@/handler/storageLocal'
  import AccountingEntrySource from './inc/AccountingEntrySource'
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import moment from 'moment'
  import FindObject from '@/components/inc/find/findObject'
  import AccountingEntryBuilder from '@/components/inc/accounting/entries'
  import AccountingBookBar from './inc/AccountingBookBar'
  import AccountingBookBadge from './inc/AccountingBookBadge'
  import AccountingBook from './inc/accountingBook'
  import AccountingInventoryModal from './inc/AccountingInventoryModal'
  import AccountingMonthLockModal from './inc/AccountingMonthLockModal'
  import AccountingIvaModal from './inc/AccountingIvaModal'
  import AccountingOpeningForm from './inc/AccountingOpeningForm'

  export default {
    components: {
      AccountingEntrySource,
      AccountingHelp,
      TabHeaderActions,
      FindObject,
      AccountingEntryBuilder,
      AccountingBookBar,
      AccountingBookBadge,
      AccountingInventoryModal,
      AccountingMonthLockModal,
      AccountingIvaModal,
      AccountingOpeningForm,
    },
    data: () => {
      return {
        filtersOpen: false,
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingEntries',
          elements: {}
        },
        table : {
          items: [],
          filters: null,
          fields: [
            {key: 'date', label: 'Fecha', class: "align-middle"},
            {key: 'book', label: 'Libro', class: "align-middle text-center", thStyle: 'width:60px'},
            {key: 'reference', label: 'Referencia', class: "align-middle"},
            {key: 'observations', label: 'Observaciones', class: "align-middle"},
            {key: 'origin', label: 'Origen', class: "align-middle text-center"},
            {key: 'total_debit', label: 'Debe', class: "align-middle text-right"},
            {key: 'total_credit', label: 'Haber', class: "align-middle text-right"},
            {key: 'f_action', label:'', class: "align-middle"},
          ],
          currentPage: 1,
          perPage: 0,
          tablePaginate: null,
        },
        tableSub : {
          items: [],
          fields: [
            {key: 'code', label: 'Código', class:'align-middle'},
            {key: 'name', label: 'Nombre', class:'align-middle'},
            {key: 'debit', label: 'Debe', class:'align-middle text-right'},
            {key: 'credit', label: 'Haber', class:'align-middle text-right'},
          ],
          filter: null,
          currentPage: 1,
          perPage: 50,
        },
        arr: {
          accounting: [],
          templates: [],
          businesses: [],
          leafAccounts: [],
          select: {
            status: [
              { value: '', text: 'Todos' },
              { value: 'balanced', text: 'Balanceado' },
              { value: 'unbalanced', text: 'Con dif.' },
            ],
            sourceType: [
              { value: '', text: 'Todos' },
              { value: 'sales', text: 'Ventas' },
              { value: 'purchase', text: 'Compras' },
              { value: 'receipts', text: 'Recibos' },
              { value: 'payments', text: 'Pagos' },
              { value: 'funds', text: 'Fondos' },
              { value: 'cheque_deposits', text: 'Depósito de cheque' },
              { value: 'cheque_credits', text: 'Acreditación de cheque' },
              { value: 'cheque_rejections', text: 'Cheque rechazado' },
              { value: 'cheque_returns', text: 'Cheque vuelto a cartera' },
              { value: 'cheque_resolutions', text: 'Rechazo resuelto' },
            ],
            origin: [
              { value: '', text: 'Todos' },
              { value: 'automatic', text: 'Auto' },
              { value: 'manual', text: 'Manual' },
              { value: 'closing', text: 'Cierre' },
              { value: 'opening', text: 'Apertura' },
              { value: 'inventory', text: 'Inventario' },
              { value: 'iva', text: 'Liquidación IVA' },
            ],
            declaredRequired: [
              { value: null, text: 'Seleccionar tipo...' },
              { value: true, text: 'Declarado (D)' },
              { value: false, text: 'No Declarado (ND)' },
            ],
          }
        },
        filters: {
          date_start: '',
          date_end: '',
          reference: '',
          accounting_accounts_id: 0,
          status: '',
          origin: '',
          source_type: '',
          business_id: 0,
          declared: '',
        },
        crud: {
          manual: {
            id: 0,
            date: '',
            reference: '',
            observations: '',
            origin: 'manual',
            detail: null,
            defaultDebe: [],
            defaultHaber: [],
            business_id: 0,
            business_obj: null,
            declared: null,
            template: '',
          }
        },
        modal: {
          template: {
            active: false,
            _id: 'accounting_template_save',
            name: '',
            keepAmounts: false,
          },
          manual: {
            active: false,
            title: 'Nuevo asiento manual',
            _id: '',
          },
          detail: {
            active: false,
            title: 'Detalle de asiento',
            _id: '',
          }
        },
        manualAccountingKey: 0,
        selectedAccounting: null,
        maxClosingDate: null,
        bookStatus: null,
        canUnlock: true,
      }
    },
    computed: {
      // Inventario, apertura y bloqueo son de un libro: con Ambos no aplican
      bookSelected() {
        return !!this.filters.business_id && this.filters.declared !== ''
      },
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      // El desbloqueo de meses usa el mismo permiso que reabrir un período en Cierre y Apertura
      this.canUnlock = Helper.hasAccessElement({ module_id: this.access.module_id, profile_id: this.access.profile_id, view_reference: 'accountingClosing' }, 'reopen_period')
      /* Fin configuracion */
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_accounting_entries', value ? 'true' : 'false')
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_accounting_entries') == 'true'
      this.filterLoad()
      this.loadBusinesses()
      this.loadLeafAccounts()
      this.loadTemplates()
    },
    methods: {
      moment,
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
        .then(response => {
          const businesses = response.data || []
          this.arr.businesses = businesses.map(b => ({ value: b.id, text: b.name }))
          // Con empresas, el selector de libro dispara la primera carga
          if(!businesses.length) {
            this.loadClosingDates()
            this.filterReport()
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      loadLeafAccounts() {
        serviceAPI.filtrarPlanCuenta()
        .then(response => {
          const leaves = []
          this.collectLeafAccounts(response.data || [], leaves)
          this.arr.leafAccounts = [
            { value: 0, text: 'Todas las cuentas' },
            ...leaves.map(a => ({ value: a.id, text: a.code + ' - ' + a.name }))
          ]
        })
      },
      collectLeafAccounts(accounts, result) {
        accounts.forEach(account => {
          const children = account.children_recurrent || account.children || []
          if(children.length) {
            this.collectLeafAccounts(children, result)
          } else {
            result.push(account)
          }
        })
      },
      loadClosingDates() {
        const params = {}
        if(this.filters.business_id) params.business_id = this.filters.business_id
        if(this.filters.declared !== '') params.declared = this.filters.declared

        serviceAPI.obtenerFechasCierre(params)
        .then(response => {
          const dates = response.data || []
          this.maxClosingDate = dates.length ? dates[0] : null
        })
      },
      onBookChange(book) {
        this.filters.business_id = book.business_id
        this.filters.declared = AccountingBook.declaredParam(book.declared)
        this.loadClosingDates()
        this.loadBookStatus()
        this.filterReportReset()
      },
      // Apertura, bloqueo e inventario del libro elegido
      loadBookStatus() {
        if(!this.bookSelected) {
          this.bookStatus = null
          return
        }
        serviceAPI.obtenerEstadoPeriodo({ business_id: this.filters.business_id, declared: this.filters.declared === '1' })
        .then(response => {
          this.bookStatus = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      onBookActionDone() {
        this.loadClosingDates()
        this.loadBookStatus()
        this.filterReportReset()
      },
      loadTemplates() {
        serviceAPI.obtenerModelos()
        .then(response => {
          this.arr.templates = response.data || []
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      // Carga las cuentas del modelo con los importes editables; la referencia y las observaciones solo si están vacías
      applyTemplate(template) {
        const toRow = line => ({ account: { id: line.accounting_accounts_id, code: line.code, name: line.name }, amount: parseFloat(line.amount) || 0 })
        this.crud.manual.defaultDebe = template.lines.filter(line => line.type == 'Debe').map(toRow)
        this.crud.manual.defaultHaber = template.lines.filter(line => line.type == 'Haber').map(toRow)
        if(!this.crud.manual.reference && template.reference) this.crud.manual.reference = template.reference
        if(!this.crud.manual.observations && template.observations) this.crud.manual.observations = template.observations
        this.crud.manual.template = template.name
        this.crud.manual.detail = null
        this.manualAccountingKey++
      },
      openSaveTemplate() {
        const lines = this.$refs.manualBuilder ? this.$refs.manualBuilder.currentLines() : []
        if(!lines.length) {
          this.$awn.alert('Cargá al menos un renglón para guardarlo como modelo')
          return
        }
        this.modal.template.name = this.crud.manual.template || ''
        this.modal.template.keepAmounts = false
        this.modal.template.active = true
      },
      saveTemplate() {
        const name = this.modal.template.name.trim()
        if(!name) return
        const keep = this.modal.template.keepAmounts
        const lines = this.$refs.manualBuilder.currentLines().map(line => Object.assign({}, line, { amount: keep ? line.amount : 0 }))
        serviceAPI.guardarModelo({
          name: name,
          reference: this.crud.manual.reference,
          observations: this.crud.manual.observations,
          lines: lines,
        })
        .then(() => {
          this.modal.template.active = false
          this.$awn.success('Modelo «' + name + '» guardado')
          this.loadTemplates()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      deleteTemplate(template) {
        this.confirmDialog("Se borra el modelo '" + template.name + "'. Los asientos ya cargados con él no cambian.", {
          title: 'Borrar modelo',
          icon: 'delete',
          danger: true,
          okTitle: 'Borrar',
        })
        .then(value => {
          if(!value) return
          serviceAPI.eliminarModelo(template.id)
          .then(() => {
            this.$awn.success('Modelo borrado')
            this.loadTemplates()
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
        })
      },
      openIva() {
        this.$refs.iva.open(this.bookStatus ? this.bookStatus.last_iva : null)
      },
      openInventory() {
        this.$refs.inventory.open(this.bookStatus ? this.bookStatus.last_inventory : null)
      },
      formatShortDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : ''
      },
      loadManualBusiness(object) {
        if(object) {
          this.crud.manual.business_obj = object
          this.crud.manual.business_id = object.id
        } else {
          this.crud.manual.business_obj = null
          this.crud.manual.business_id = 0
        }
      },
      isPeriodLocked(date) {
        if(!this.maxClosingDate || !date) return false
        return date.substring(0, 10) <= this.maxClosingDate
      },
      entryRowClass(item) {
        if(!item) return ''
        return item.is_balanced ? 'accounting-entry-row' : 'accounting-entry-row table-danger'
      },
      subTableRowClass(item) {
        if (!item) return ''
        return item.type === 'Debe' ? 'table-warning' : 'table-info'
      },
      originVariant(origin) {
        const map = { manual: 'info', closing: 'dark', opening: 'success', inventory: 'warning', iva: 'warning' }
        return map[origin] || 'secondary'
      },
      originLabel(origin) {
        const map = { manual: 'Manual', closing: 'Cierre', opening: 'Apertura', inventory: 'Inventario', iva: 'IVA' }
        return map[origin] || 'Automático'
      },
      openDetail(item) {
        this.selectedAccounting = item
        this.tableSub.items = item.details || []
        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = 'Detalle de asiento: ' + item.reference
        this.modal.detail.active = true
      },
      filterLoad() {
        this.filters.date_start = moment().startOf('month').format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
      },
      filterReportReset() {
        this.table.currentPage = 1
        this.tableSub.items = []
        this.filterReport()
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR',{style:'currency',currency: 'ARS'}).format(value || 0)
      },
      openManualAccounting(item) {
        this.crud.manual.id = item ? item.id : 0
        this.crud.manual.date = item ? item.date : moment().format('YYYY-MM-DD')
        this.crud.manual.reference = item ? item.reference : ''
        this.crud.manual.observations = item ? item.observations : ''
        this.crud.manual.origin = item ? item.origin : 'manual'
        this.crud.manual.template = ''
        this.crud.manual.detail = null
        this.crud.manual.defaultDebe = []
        this.crud.manual.defaultHaber = []
        // Uno nuevo arranca en el libro que se está viendo
        const book = AccountingBook.state
        this.crud.manual.business_id = item ? (item.business_id || 0) : (book.business_id || (this.arr.businesses.length ? this.arr.businesses[0].value : 0))
        this.crud.manual.business_obj = null
        this.crud.manual.declared = item ? (item.declared !== null && item.declared !== undefined ? item.declared : null) : book.declared

        if(item && item.details) {
          item.details.forEach(element => {
            let row = {
              account: {
                id: element.accounting_accounts_id,
                code: element.accounting_accounts_code,
                name: element.accounting_accounts_name,
              },
              amount: parseFloat(element.total),
            }

            if(element.type == 'Debe') {
              this.crud.manual.defaultDebe.push(row)
            }

            if(element.type == 'Haber') {
              this.crud.manual.defaultHaber.push(row)
            }
          })
        }

        this.manualAccountingKey = this.manualAccountingKey + 1

        this.modal.manual._id = Math.random().toString(36).slice(2, 8)
        this.modal.manual.title = item ? (item.origin == 'opening' ? 'Editar asiento de apertura' : 'Editar asiento manual') : 'Nuevo asiento manual'
        this.modal.manual.active = true
      },
      loadManualAccountingDetail(object) {
        this.crud.manual.detail = object
      },
      saveManualAccounting() {
        if(!this.crud.manual.business_id) {
          this.$awn.alert('Debe seleccionar la empresa')
          return false
        }

        if(this.crud.manual.declared === null || this.crud.manual.declared === undefined) {
          this.$awn.alert('Debe seleccionar el tipo (Declarado / No Declarado)')
          return false
        }

        if(!this.crud.manual.date) {
          this.$awn.alert('No se cargó la fecha')
          return false
        }

        if(!this.crud.manual.reference) {
          this.$awn.alert('No se cargó la referencia')
          return false
        }

        if(!this.crud.manual.detail) {
          this.$awn.alert('Revise el asiento contable')
          return false
        }

        let loader = this.$loading.show()
        let result = this.crud.manual.id ? serviceAPI.editarAsiento(this.crud.manual) : serviceAPI.agregarAsiento(this.crud.manual)

        result
        .then(() => {
          loader.hide()
          this.modal.manual.active = false
          this.filterReportReset()
          this.$awn.success('Asiento manual guardado con éxito')
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: options.icon || (danger ? 'warning' : 'help') } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      revertApproved(item) {
        this.confirmDialog("¿Desea borrar el asiento de '" + item.reference + "'? El comprobante vuelve a Asientos Pendientes y gestión puede volver a modificarlo.", {
          title: 'Borrar asiento',
          icon: 'undo',
          danger: true,
          okTitle: 'Borrar',
        })
        .then(value => {
          if(value) {
            let loader = this.$loading.show()
            serviceAPI.borrarAsientoAprobado(item.id)
            .then(() => {
              loader.hide()
              this.$awn.success('Asiento borrado')
              this.filterReport()
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
      },
      deleteManualAccounting(item) {
        const isInventory = item.origin == 'inventory'
        const isIva = item.origin == 'iva'
        const message = isInventory
          ? "Se borra el asiento de inventario '" + item.reference + "'. Mercaderías vuelve al saldo anterior y podés recalcularlo."
          : (isIva ? "Se borra la liquidación de IVA '" + item.reference + "'. Las cuentas de IVA vuelven a su saldo y podés liquidar de nuevo." : "¿Desea eliminar el asiento manual '" + item.reference + "'?")
        this.confirmDialog(message, {
          title: isInventory ? 'Borrar asiento de inventario' : (isIva ? 'Borrar liquidación de IVA' : 'Eliminar asiento manual'),
          icon: 'delete',
          danger: true,
          okTitle: 'Eliminar',
        })
        .then(value => {
          if(value) {
            let loader = this.$loading.show()
            serviceAPI.eliminarAsiento(item.id)
            .then(() => {
              loader.hide()
              this.filterReportReset()
              this.$awn.success(isInventory ? 'Asiento de inventario borrado' : (isIva ? 'Liquidación de IVA borrada' : 'Asiento manual eliminado'))
              this.loadBookStatus()
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
      },
      filterReport(){
        let loader = this.$loading.show()

        var result = serviceAPI.filtrarAsientos(this.filters, this.table.currentPage)

        result.then((response) => {
          loader.hide()
          this.table.items = response.data.data || []
          this.table.tablePaginate = response.data

          this.arr.accounting = response.data.data || []

          this.tableSub.items = []
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        });
      },
    }
  }
</script>
<style scoped>
  ::v-deep .accounting-entry-row {
    cursor: pointer;
  }
  .filter-find-object ::v-deep fieldset {
    margin-bottom: 0 !important;
  }

  .accounting-entries-amount {
    font-size: 13px;
  }

  .filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .entry-templates {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
  }
  .entry-templates >>> .entry-templates__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 260px;
  }
  .entry-templates__delete {
    color: var(--app-color-danger);
    padding: 0 4px;
  }
</style>
